import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { SubmeshData } from '@models/classes';
import { NormalSmoothingFeature } from '@models/classes/features/normal-smoothing-feature';
import { Token } from '@models/classes/token';
import { ITrackedClass } from '@models/interfaces/tracked-class';
import { addArrays } from '@view/helpers';
import { SubmeshVertexData } from '@view/helpers/geometry-helper';
import { FeatureRenderer } from './feature';

export class NormalSmoothingRenderer extends FeatureRenderer<NormalSmoothingFeature> {
    modelToken = Token.NormalSmoothingFeature;
    vertexNeighbors = new Map<string, number[]>();
    planeNormals = new Map<number, number[]>();
    vectorA = new Vector3();
    vectorB = new Vector3();
    vectorC = new Vector3();
    normalVector = new Vector3();
    totalSelectedIndices = 0;
    updateMeshGeometry(
        feature: ITrackedClass<NormalSmoothingFeature>,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);
        let vertexGroups = [];

        //if no faces are selected, select all faces to smooth entire mesh.
        if (feature.vertexGroups.length === 0 && newVertexData.indices) {
            for (let i = 0; i < newVertexData.indices.length; i += 6) {
                vertexGroups.push({ startIndex: i, endIndex: i + 6 });
            }
        } else {
            vertexGroups = feature.vertexGroups.sort((groupA, groupB) => groupA.startIndex - groupB.startIndex);
        }

        if (newVertexData.indices) {
            const planeNormal = new Vector3();
            let normals: number[];
            let positions: number[];
            let uvs: number[];
            let indices: number[];
            const newPositions: number[] = [];
            const newNormals: number[] = [];
            const newUvs: number[] = [];
            const submeshDataArray: SubmeshData[] = [];
            if (newVertexData.normals) {
                normals = newVertexData.normals as number[];
            } else if (vertexData.normals) {
                normals = Array.from(vertexData.normals) as number[];
            } else {
                normals = [];
            }

            if (newVertexData.positions) {
                positions = newVertexData.positions as number[];
            } else if (vertexData.positions) {
                positions = Array.from(vertexData.positions) as number[];
            } else {
                positions = [];
            }

            if (newVertexData.uvs) {
                uvs = newVertexData.uvs as number[];
            } else if (vertexData.uvs) {
                uvs = Array.from(vertexData.uvs) as number[];
            } else {
                uvs = [];
            }

            if (newVertexData.indices) {
                indices = newVertexData.indices as number[];
            } else if (vertexData.indices) {
                indices = Array.from(vertexData.indices) as number[];
            } else {
                indices = [];
            }

            this.vertexNeighbors.clear();
            this.planeNormals.clear();
            this.totalSelectedIndices = 0;
            vertexGroups.forEach(vertexGroup => {
                this.totalSelectedIndices += vertexGroup.endIndex - vertexGroup.startIndex;
            });

            if (feature.smooth) {
                //looping through the vertex groups and finding all the neighboring faces
                vertexGroups.forEach(vertexGroup => {
                    const planeCoordinates = [];
                    const planePositions = [];
                    for (let i = vertexGroup.startIndex; i < vertexGroup.endIndex; i++) {
                        if (positions && indices) {
                            let key = '';
                            for (let j = 0; j < 3; j++) {
                                const vertexIndex = indices[i];
                                const coordinate = positions[vertexIndex * 3 + j];
                                planePositions.push(coordinate);
                                if (j == 2) {
                                    planeCoordinates.push([...planePositions]);
                                    planePositions.clear();
                                }
                                key += String(coordinate);
                            }

                            if (planeCoordinates.length == 3) {
                                this.calculateNormalToRef(planeCoordinates, planeNormal);
                                this.planeNormals.set(Math.floor(i / 3), planeNormal.asArray());
                                planeCoordinates.clear();
                            }

                            let neighbor = this.vertexNeighbors.get(key);
                            if (neighbor) {
                                neighbor.push(i);
                                this.vertexNeighbors.set(key, neighbor);
                            } else {
                                neighbor = [];
                                neighbor.push(i);
                                this.vertexNeighbors.set(key, neighbor);
                            }
                        }
                    }
                });

                this.vertexNeighbors.forEach(position => {
                    const newNormal = this.calculateSmoothedUnitVector(position);

                    position.forEach(index => {
                        if (normals && indices) {
                            for (let i = 0; i < 3; i++) {
                                normals[indices[index] * 3 + i] = newNormal[i];
                            }
                        }
                    });
                });
            } else {
                //looping through the selected faces to get the face normals
                vertexGroups.forEach(vertexGroup => {
                    const planeCoordinates = [];
                    const planePositions = [];
                    for (let i = vertexGroup.startIndex; i < vertexGroup.endIndex; i++) {
                        if (positions && indices) {
                            for (let j = 0; j < 3; j++) {
                                const vertexIndex = indices[i];
                                const coordinate = positions[vertexIndex * 3 + j];
                                planePositions.push(coordinate);
                                if (j == 2) {
                                    planeCoordinates.push([...planePositions]);
                                    planePositions.clear();
                                }
                            }

                            if (planeCoordinates.length == 3) {
                                this.calculateNormalToRef(planeCoordinates, planeNormal);
                                const planeNormalArray = this.calculateUnitVector(planeNormal.asArray());
                                this.planeNormals.set(i, planeNormalArray);
                                this.planeNormals.set(i - 1, planeNormalArray);
                                this.planeNormals.set(i - 2, planeNormalArray);
                                planeCoordinates.clear();
                            }
                        }
                    }
                });

                //looping through it again to change/add the normals
                let totalVertices = 0;
                if (positions) {
                    totalVertices = positions.length / 3;
                }
                const visitedVertices: boolean[] = [];
                vertexGroups.forEach(vertexGroup => {
                    for (let i = vertexGroup.startIndex; i < vertexGroup.endIndex; i++) {
                        const vertex = indices[i];
                        const planeNormal = this.planeNormals.get(i);
                        const currentNormal: number[] = [];
                        const currentPosition: number[] = [];
                        const currentUv: number[] = [];

                        for (let i = 0; i < 3; i++) {
                            currentNormal.push(normals[vertex * 3 + i]);
                            currentPosition.push(positions[vertex * 3 + i]);
                        }
                        for (let i = 0; i < 2; i++) {
                            currentUv.push(uvs[vertex * 2 + i]);
                        }

                        //vertex is already visited, therefor we add a new one.
                        //else: replace the existing vertex
                        if (visitedVertices[vertex]) {
                            for (let i = 0; i < 3; i++) {
                                if (planeNormal) {
                                    newNormals.push(planeNormal[i]);
                                    newPositions.push(currentPosition[i]);
                                }
                            }
                            for (let i = 0; i < 2; i++) {
                                newUvs.push(currentUv[i]);
                            }

                            indices[i] = totalVertices;
                            totalVertices += 1;

                            //handling submesh data
                            if (vertexData.submeshData) {
                                for (let i = 0; i < vertexData.submeshData.length; i++) {
                                    const submeshData = vertexData.submeshData[i];
                                    if (
                                        submeshData.verticesStart <= vertex &&
                                        submeshData.verticesStart + submeshData.verticesCount > vertex
                                    ) {
                                        submeshData.verticesCount += 1;
                                    } else if (submeshData.verticesStart > vertex) {
                                        submeshData.verticesStart += 1;
                                    }
                                    submeshData.indexCount = (positions.length + newPositions.length) / 3;
                                    submeshDataArray[i] = submeshData;
                                }
                            }
                        } else {
                            visitedVertices[vertex] = true;
                            if (planeNormal) {
                                for (let i = 0; i < 3; i++) {
                                    normals[vertex * 3 + i] = planeNormal[i];
                                }
                            }
                        }
                    }
                });
                const resultPositions = new Float32Array(positions.length + newPositions.length);
                resultPositions.set(positions);
                resultPositions.set(newPositions, positions.length);
                const resultNormals = new Float32Array(normals.length + newNormals.length);
                resultNormals.set(normals);
                resultNormals.set(newNormals, normals.length);
                const resultUvs = new Float32Array(uvs.length + newUvs.length);
                resultUvs.set(uvs);
                resultUvs.set(newUvs, uvs.length);
                newVertexData.positions = resultPositions;
                newVertexData.normals = resultNormals;
                newVertexData.uvs = resultUvs;
                newVertexData.indices = indices;
                newVertexData.submeshData = submeshDataArray;
            }
        }
        this.viewer.updateNormalSmoothingWarnings(feature, this.totalSelectedIndices / 3, false);
        return newVertexData;
    }

    update3dInternal() {}

    public delete3d(node: ITrackedClass<NormalSmoothingFeature>, preserveCache?: boolean | undefined): void {
        this.viewer.updateNormalSmoothingWarnings(node, 0, true);
        super.delete3d(node);
    }
    //Formula for normal of a plane: (vectorA-VectorB) x (VectorB-VectorC)
    calculateNormalToRef(coordinates: number[][], normalVector: Vector3) {
        Vector3.FromArrayToRef(coordinates[0], 0, this.vectorA);
        Vector3.FromArrayToRef(coordinates[1], 0, this.vectorB);
        Vector3.FromArrayToRef(coordinates[2], 0, this.vectorC);
        this.vectorA.subtractInPlace(this.vectorB);
        this.vectorB.subtractInPlace(this.vectorC);
        return Vector3.CrossToRef(this.vectorB, this.vectorA, normalVector);
    }

    calculateSmoothedUnitVector(vertices: number[]) {
        let result: number[] = [];
        vertices.forEach(vertex => {
            const normalVector = this.planeNormals.get(Math.floor(vertex / 3));
            if (normalVector) {
                result = addArrays(result, normalVector);
            }
        });
        return this.calculateUnitVector(result);
    }

    calculateUnitVector(vector: number[]) {
        let divider = 0;
        const result: number[] = [];
        vector.forEach(element => {
            divider += element * element;
        });
        divider = Math.sqrt(divider);
        vector.forEach(element => {
            result.push(element / divider);
        });
        return result;
    }
}
